import { i18n } from 'app/utils/i18n';
import { OverviewTable } from 'app/components/editor/subtoolbar/tags/Overview/OverviewTable/OverviewTable';
import { OverviewHeader } from 'app/components/editor/subtoolbar/tags/Overview/OverviewHeader/OverviewHeader';
import { useTagsByDocumentPaginated, useDocumentTaxonomyConfig, TagWithMetadata, PaginatedTagsParams } from 'app/api/tag-queries';
import { FilterGroup, InitialFilter, TagFilters } from 'app/components/editor/sidebar/tags/TagsFilter';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { formatTaxonomyName } from 'app/utils/tag-utils';
import Pagination from 'app/components/Pagination';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

interface OverviewProps {
  documentId: string;
  onTagDetailsClick: (tag: TagWithMetadata) => void;
}

export const Overview = ({ documentId, onTagDetailsClick }: OverviewProps) => {
  const superId = documentId ? parseInt(documentId, 10) : undefined;
  const history = useHistory();
  const location = useLocation<{ tagFilter?: InitialFilter }>();

  const initialFilter = useRef(location.state?.tagFilter ?? null).current;

  // Clear location.state after reading so re-renders/back navigation don't re-seed
  useMemo(() => {
    if (location.state?.tagFilter) {
      history.replace(location.pathname, {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Server-side pagination & filter state
  const [currentPage, setCurrentPage] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState<TagFilters>(() => {
    const base: TagFilters = { status: [], validated: [], type: [], taxonomy: [] };
    if (initialFilter) {
      base[initialFilter.type] = [initialFilter.value];
    }
    return base;
  });

  // Debounce search input
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSetSearchInput = useCallback((value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setCurrentPage(0);
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  // Build query params from filter state
  const queryParams: PaginatedTagsParams = useMemo(() => {
    const params: PaginatedTagsParams = {
      page: currentPage,
      pageSize: PAGE_SIZE,
      include: ['metadata', 'concept', 'taxonomy', 'unit', 'context'],
    };
    if (debouncedSearch) params.search = debouncedSearch;
    if (filters.status.length) params.status = filters.status;
    if (filters.validated.length) params.validationStatus = filters.validated;
    if (filters.type.length) params.type = filters.type;
    if (filters.taxonomy.length) params.taxonomy = filters.taxonomy;
    if (filters.currency?.length) params.currency = filters.currency;
    if (filters.context?.length) params.contextId = filters.context;
    return params;
  }, [currentPage, debouncedSearch, filters]);

  const { data } = useTagsByDocumentPaginated(superId, queryParams);
  const { data: taxonomyConfig } = useDocumentTaxonomyConfig(superId);

  const rows = data?.rows ?? [];
  const totalCount = data?.count ?? 0;
  const serverStats = useMemo(() => data?.stats ?? { total: 0, approved: 0, draft: 0, needsAttention: 0, invalid: 0, mandatory: 0 }, [data?.stats]);
  const totalMandatoryTags = data?.totalMandatoryTags ?? 0;

  const taxonomyFilterOptions = useMemo(() => {
    if (!taxonomyConfig?.taxonomySelections?.length) return [];
    return taxonomyConfig.taxonomySelections.map((ts) => ({
      label: ts.taxonomy?.name ? formatTaxonomyName(ts.taxonomy.name) : ts.taxonomyId,
      value: ts.taxonomy?.name || ts.taxonomyId,
    }));
  }, [taxonomyConfig?.taxonomySelections]);

  const filterGroups: FilterGroup[] = useMemo(() => {
    const groups: FilterGroup[] = [
      {
        label: i18n('tag.taxonomy.plural'),
        value: 'taxonomy',
        icon: <i className="fa-solid fa-layer-group" />,
        options: taxonomyFilterOptions,
      },
      {
        label: i18n('tag.highlighted.option.status'),
        value: 'status',
        icon: <i className="fa-solid fa-spinner" />,
        options: [
          { label: i18n('tag.status.approved'), value: 'approved' },
          { label: i18n('tag.status.draft'), value: 'draft' },
          { label: i18n('tag.status.need_attention'), value: 'need_attention' },
          { label: i18n('tag.status.verified'), value: 'verified' },
        ],
      },
      {
        label: i18n('tag.validation.label'),
        value: 'validated',
        icon: <i className="fa-solid fa-flag" />,
        options: [
          { label: i18n('tag.status.invalid'), value: 'invalid' },
          { label: i18n('tag.status.unvalidated'), value: 'unvalidated' },
          { label: i18n('tag.status.valid'), value: 'valid' },
        ],
      },
      {
        label: i18n('tag.type'),
        value: 'type',
        icon: <i className="fa-solid fa-tags" />,
        options: [
          { label: i18n('tag.type.numeric'), value: 'nonFraction' },
          { label: i18n('tag.type.textBlock'), value: 'nonNumeric' },
        ],
      },
    ];

    if (initialFilter) {
      const hasGroup = groups.some((g) => g.value === initialFilter.type);
      if (!hasGroup) {
        groups.push({
          label: initialFilter.type === 'currency' ? i18n('tag.monetary.label') : i18n('tag.date.label'),
          value: initialFilter.type,
          options: [{ label: initialFilter.label, value: initialFilter.value }],
        });
      }
    }

    return groups;
  }, [taxonomyFilterOptions, initialFilter]);

  const onAddFilter = useCallback((group: string, value: string) => {
    setFilters((prev) => {
      const prevGroup = prev[group] || [];
      if (prevGroup.includes(value)) {
        return { ...prev, [group]: prevGroup.filter((item) => item !== value) };
      } else {
        return { ...prev, [group]: [...prevGroup, value] };
      }
    });
    setCurrentPage(0);
  }, []);

  const onRemoveFilter = useCallback((group: string, value: string) => {
    setFilters((prev) => {
      const prevGroup = prev[group] || [];
      return { ...prev, [group]: prevGroup.filter((item) => item !== value) };
    });
    setCurrentPage(0);
  }, []);

  const tagCategories = [
    i18n('tag.name'),
    i18n('tag.type'),
    i18n('taxonomy.title'),
    i18n('tag.highlighted.option.status'),
    i18n('tag.validation.label'),
    i18n('tag.mandatory.label'),
    i18n('tag.ai.origin.label'),
    i18n('all.tags.page'),
  ];

  const totalTags = serverStats.total;

  const stats = useMemo(
    () => [
      { label: i18n('tag.total.tags'), value: serverStats.total, type: 'total' },
      { label: i18n('tag.mandatory.label'), value: serverStats.mandatory, type: 'mandatory' },
      { label: i18n('tag.status.approved'), value: serverStats.approved, type: 'approved' },
      { label: i18n('tag.status.draft'), value: serverStats.draft, type: 'draft' },
      { label: i18n('tag.status.need_attention'), value: serverStats.needsAttention, type: 'need_attention' },
      { label: i18n('tag.status.invalid'), value: serverStats.invalid, type: 'invalid' },
    ],
    [serverStats]
  );

  const paginationPage = useMemo(
    () => ({
      number: currentPage,
      totalPages: Math.ceil(totalCount / PAGE_SIZE),
      totalElements: totalCount,
      size: PAGE_SIZE,
    }),
    [currentPage, totalCount]
  );

  return (
    <div>
      <OverviewHeader
        stats={stats}
        totalTags={totalTags}
        totalMandatoryTags={totalMandatoryTags}
        searchInput={searchInput}
        setSearchInput={handleSetSearchInput}
        filters={filters}
        onAddFilter={onAddFilter}
        onRemoveFilter={onRemoveFilter}
        filterGroups={filterGroups}
      />

      <OverviewTable categories={tagCategories} tableData={rows} onTagDetailsClick={onTagDetailsClick} pageSize={PAGE_SIZE} />

      <div className="d-flex justify-content-center mt-3">
        <Pagination onPaginate={(page: number) => setCurrentPage(page)} page={paginationPage} pageSize={PAGE_SIZE} />
      </div>
    </div>
  );
};
